import { zodResolver } from '@hookform/resolvers/zod';
import { ThemeProvider } from '@mui/material';
import Typography from '@mui/material/Typography';
import { ApiError } from '@packages/shared-ui/errors';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@packages/shared-ui/v4/button';
import {
  type PlaceResult,
  RhfAddressField,
  SubmitErrorMessage,
  TextFieldSize,
} from '@packages/shared-ui/v4/form';
import { v4 } from '@packages/themes/v4';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { postServiceTerritory } from '@/clients/pxe-backend/pxeBackendClient';
import Link from '@/components/Link/Link';
import {
  type Contentful,
  isInternalLink,
} from '@/contentful/contentful-exports';
import {
  autocompleteAddressValidator,
  InputName,
} from '@/utils/validation/inputs';
import {
  type EligibilityCheckErrorType,
  trackEligibilityCheckError,
  trackEligibilityCheckResult,
  trackEligibilityCheckSubmitted,
} from './analytics';
import useStyles from './EligibilityCheckForm.styles';

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_API_KEY ?? '';
const BAD_REQUEST_STATUS = 400;
const responsiveFieldSize = {
  xs: TextFieldSize.Small,
  md: TextFieldSize.Large,
} as const;

const eligibilityFormSchema = z.object(autocompleteAddressValidator);
type EligibilityFormData = z.infer<typeof eligibilityFormSchema>;

export interface StrandedIntakeContent extends Contentful {
  fields: {
    title?: string;
    eligibleRedirectUrl: string;
    ineligibleRedirectUrl: string;
    addressFieldLabel?: string;
    addressFieldPlaceholder?: string;
    ctaLabel?: string;
    invalidAddressError?: string;
    serviceErrorLabel?: string;
    secondaryCTALabel?: string;
    secondaryCtaUrl?: string;
  };
}

interface EligibilityCheckFormProps {
  content: StrandedIntakeContent['fields'];
}

const getErrorType = (error: unknown): EligibilityCheckErrorType =>
  error instanceof ApiError && error.statusCode === BAD_REQUEST_STATUS
    ? 'validation'
    : 'service';

const EligibilityCheckForm = ({ content }: EligibilityCheckFormProps) => {
  const { classes } = useStyles();
  const router = useRouter();
  const [place, setPlace] = useState<PlaceResult>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorType, setErrorType] = useState<EligibilityCheckErrorType>();
  const {
    eligibleRedirectUrl,
    ineligibleRedirectUrl,
    addressFieldLabel,
    addressFieldPlaceholder,
    ctaLabel,
    invalidAddressError,
    serviceErrorLabel,
    secondaryCTALabel,
    secondaryCtaUrl,
  } = content;

  const form = useForm<EligibilityFormData>({
    resolver: zodResolver(eligibilityFormSchema),
    mode: 'onTouched',
    defaultValues: { [InputName.AUTOCOMPLETE_ADDRESS]: '' },
  });

  const redirect = async (url: string) => {
    if (isInternalLink(url)) {
      await router.push(url);
      return;
    }
    window.location.assign(url);
  };

  const handleAddressChange = () => {
    setPlace(undefined);
    setErrorType(undefined);
  };

  const handleSubmit = async () => {
    if (!place) return;

    const { formattedAddress, address, location } = place;
    const { zipCode, state } = address;

    setErrorType(undefined);
    setIsSubmitting(true);
    trackEligibilityCheckSubmitted({ zip: zipCode, state });

    try {
      const { isInService } = await postServiceTerritory({
        formattedAddress,
        address,
        location,
      });
      trackEligibilityCheckResult({ zip: zipCode, isInService });
      await redirect(isInService ? eligibleRedirectUrl : ineligibleRedirectUrl);
    } catch (error) {
      const type = getErrorType(error);
      trackEligibilityCheckError(type);
      setErrorType(type);
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorMessage =
    errorType === 'validation'
      ? (invalidAddressError ??
        'We could not find a zip code for that address. Please check it and try again.')
      : (serviceErrorLabel ?? 'Something went wrong. Please try again.');

  return (
    <ThemeProvider theme={v4}>
      <div className={classes.root}>
        <FormProvider {...form}>
          <form
            className={classes.form}
            onSubmit={form.handleSubmit(handleSubmit)}
            noValidate
          >
            <div className={classes.field}>
              <RhfAddressField<EligibilityFormData>
                name={InputName.AUTOCOMPLETE_ADDRESS}
                label={addressFieldLabel ?? 'Address'}
                placeholder={addressFieldPlaceholder}
                fullWidth
                inputSize={responsiveFieldSize}
                googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                onPlaceSelected={setPlace}
                onInputChange={handleAddressChange}
                data-testid='eligibility-check-address-input'
              />
            </div>
            <Button
              type='submit'
              variant={ButtonVariant.PRIMARY}
              size={{ xs: ButtonSize.SM, md: ButtonSize.MD }}
              disabled={!place}
              isLoading={isSubmitting}
              className={classes.submitButton}
              data-testid='eligibility-check-submit'
            >
              {ctaLabel ?? 'Get started'}
            </Button>
          </form>
        </FormProvider>
        {errorType && (
          <SubmitErrorMessage testId='eligibility-check-error'>
            {errorMessage}
          </SubmitErrorMessage>
        )}
        {secondaryCtaUrl && (
          <Typography
            component={Link}
            href={secondaryCtaUrl}
            variant='bodyMd'
            color='semantic.text.primary'
            className={classes.secondaryLink}
          >
            {secondaryCTALabel}
          </Typography>
        )}
      </div>
    </ThemeProvider>
  );
};

export default EligibilityCheckForm;
